import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconFromSet,
  IconPicker,
  IconValue
} from "@gettactic/components";
import { TacticIconsDictionary } from "@gettactic/components/src/icons/tactic-icons/dictionary";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@gettactic/components/src/primitives/Popover";
import { useDisclosure } from "@gettactic/helpers";
import {
  Armchair,
  ArrowDown01,
  AudioWaveform,
  Boxes,
  BoxSelect,
  ChevronUp,
  Circle,
  Eclipse,
  Grid3x3,
  Hand,
  Keyboard,
  Minus,
  MousePointer,
  Ruler,
  Sparkles,
  Spline,
  Square,
  SquareDashedBottom,
  Type
} from "lucide-react";
import React from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { Furniture } from "../../../../furniture-list";
import { DrawShape } from "../../../../types";
import { FurnitureSelectionModal } from "../../../FurnitureSelectionModal";
import { TooltipProvider } from "../../../ui/tooltip";
import { useMapCanvas } from "../../contexts/MapCanvasContext";
import {
  useMapEditorActions,
  useMapEditorProps,
  useMapEditorTool
} from "../../contexts/MapEditorContext";
import { MapEditorTool } from "../../types";
import { EditorTooltip } from "../controls/EditorTooltip";
import { useEditorShortcuts } from "../shortcuts/EditorShortcutContext/useEditorShortcuts";
import { KeyboardShortcutsDialog } from "../shortcuts/KeyboardShortcutsDialog";
import { ToolbarButton } from "./ToolbarButton";

interface BottomToolbarProps {
  activeTool: MapEditorTool;
  onToolChange: (tool: MapEditorTool) => void;
}

const classes = {
  ToolBar:
    "fixed bottom-5 left-1/2 z-sticky shadow-lg bg-background-primary rounded-xl border border-neutral-200 -translate-x-1/2 flex items-center gap-0.5 p-1",
  Separator: "w-px h-[22px] bg-neutral-200 mx-1 shrink-0",
  ChevronButton:
    "flex items-center justify-center h-[34px] w-5 rounded-md hover:bg-neutral-100 transition-colors text-neutral-400 hover:text-neutral-600",
  PopoverContent: "bg-background-primary p-0 w-[550px]",
  PopoverArrow: "fill-muted"
};

const ToolsPickerToolbarComponent: React.FC<BottomToolbarProps> = ({
  activeTool,
  onToolChange
}) => {
  const { onUploadIcon, mapIcons } = useMapCanvas();
  const { iconToolSelectedIcon, furnitureToolSelectedFurniture, drawTool } =
    useMapEditorTool();
  const {
    setIconToolSelectedIcon,
    setFurnitureToolSelectedFurniture,
    setDrawTool
  } = useMapEditorActions();
  const { restrictedMode } = useMapEditorProps();
  const { shortcuts } = useEditorShortcuts();
  const iconPickerDisclosure = useDisclosure();
  const selectionToolChangeDisclosure = useDisclosure();
  const furniturePickerDisclosure = useDisclosure();
  const drawToolChangeDisclosure = useDisclosure();
  const [shortcutsDialogOpen, setShortcutsDialogOpen] = React.useState(false);

  // Merge API icons (category="icon") into the tactic icon set
  const apiIconCustomSets = React.useMemo(() => {
    const apiIcons = mapIcons?.filter((i) => i.category === "icon") || [];
    if (apiIcons.length === 0) return TacticIconsDictionary;

    const tacticSet = TacticIconsDictionary.tactic;
    const mergedIcons = { ...tacticSet.icons };
    const mergedKeys = [...tacticSet.keys];
    const mergedUrls: Record<string, string> = { ...tacticSet.urls };

    for (const icon of apiIcons) {
      mergedIcons[icon.name] = icon.name;
      mergedKeys.push(icon.name);
      mergedUrls[icon.name] = icon.icon_url;
    }

    return {
      ...TacticIconsDictionary,
      tactic: {
        icons: mergedIcons,
        keys: mergedKeys,
        urls: mergedUrls
      }
    };
  }, [mapIcons]);

  // Build furniture list from API icons (non-"icon" categories)
  const apiFurnitureList = React.useMemo(() => {
    const furnitureIcons = mapIcons?.filter((i) => i.category !== "icon") || [];
    return furnitureIcons.map(
      (icon): Furniture => ({
        id: icon.id,
        name: icon.name,
        url: icon.icon_url,
        category: icon.category
      })
    );
  }, [mapIcons]);

  useHotkeys("shift+/", () => setShortcutsDialogOpen((prev) => !prev));

  const handleIconToolClick = () => {
    if (activeTool === "icon" || !iconToolSelectedIcon) {
      iconPickerDisclosure.open();
    }
    onToolChange("icon");
  };

  const handleIconSelection = (icon: IconValue) => {
    setIconToolSelectedIcon(icon);
    iconPickerDisclosure.close();
  };

  const handleFurnitureToolClick = () => {
    if (activeTool === "furniture" || !furnitureToolSelectedFurniture) {
      furniturePickerDisclosure.open();
    }
    onToolChange("furniture");
  };

  const handleFurnitureSelection = (furniture: Furniture) => {
    setFurnitureToolSelectedFurniture(furniture);
    furniturePickerDisclosure.close();
  };

  const [selectionToolSelected, setSelectionToolSelected] =
    React.useState<MapEditorTool>("select");

  const [nodeToolSelected, setNodeToolSelected] =
    React.useState<MapEditorTool>("spot");

  const [drawToolSelected, setDrawToolSelected] = React.useState<DrawShape>(
    drawTool.selectedShape
  );

  const [wallToolSelected, setWallToolSelected] =
    React.useState<MapEditorTool>("wall-path");

  const handleSelectionToolChange = (tool: MapEditorTool) => {
    if (tool !== "draw-select" && tool !== "select") return;
    setSelectionToolSelected(tool);
    selectionToolChangeDisclosure.close();
    onToolChange(tool);
  };

  const handleNodeToolChange = (tool: MapEditorTool) => {
    if (tool !== "spot" && tool !== "node-style") return;
    setNodeToolSelected(tool);
    selectionToolChangeDisclosure.close();
    onToolChange(tool);
  };

  const handleDrawToolChange = (shape: DrawShape) => {
    setDrawToolSelected(shape);
    setDrawTool(shape);
    onToolChange(shape === "zone" ? "zone" : "draw");
    drawToolChangeDisclosure.close();
  };

  const handleWallToolChange = (tool: MapEditorTool) => {
    if (tool !== "wall" && tool !== "wall-rect" && tool !== "wall-path") return;
    setWallToolSelected(tool);
    onToolChange(tool);
  };

  return (
    <>
      <TooltipProvider delayDuration={150} skipDelayDuration={0}>
        <div className={classes.ToolBar}>
          {/* Selection tools */}
          <div className="flex items-center">
            {selectionToolSelected === "select" && (
              <EditorTooltip
                asChild
                message={"Selection Tool"}
                shortcut={shortcuts.global.selectionTool}
              >
                <ToolbarButton
                  onClick={onToolChange.bind(null, "select")}
                  isActive={activeTool === "select"}
                >
                  <MousePointer size={18} />
                </ToolbarButton>
              </EditorTooltip>
            )}
            {selectionToolSelected === "draw-select" && (
              <EditorTooltip
                asChild
                message={"Draw Selection Tool"}
                shortcut={shortcuts.global.drawSelectTool}
              >
                <ToolbarButton
                  onClick={onToolChange.bind(null, "draw-select")}
                  isActive={activeTool === "draw-select"}
                >
                  <AudioWaveform size={18} />
                </ToolbarButton>
              </EditorTooltip>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={classes.ChevronButton}
                  aria-label="Open selection tool options"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center">
                <DropdownMenuItem
                  onClick={handleSelectionToolChange.bind(null, "select")}
                >
                  <div className="flex items-center gap-2">
                    <MousePointer size={18} />
                    Selection Tool
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleSelectionToolChange.bind(null, "draw-select")}
                >
                  <div className="flex items-center gap-2">
                    <AudioWaveform size={18} />
                    Draw Selection
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <EditorTooltip
            asChild
            message={"Pan Tool"}
            shortcut={shortcuts.global.panTool}
          >
            <ToolbarButton
              onClick={onToolChange.bind(null, "pan")}
              isActive={activeTool === "pan"}
            >
              <Hand size={18} />
            </ToolbarButton>
          </EditorTooltip>

          <div className={classes.Separator} />

          {restrictedMode ? null : (
            <>
              {/* Draw tools */}
              <div className="flex items-center">
                <EditorTooltip
                  asChild
                  message={
                    drawToolSelected === "circle"
                      ? "Circle"
                      : drawToolSelected === "zone"
                        ? "Zone"
                        : "Rectangle"
                  }
                  shortcut={
                    drawToolSelected === "circle"
                      ? shortcuts.global.circleTool
                      : shortcuts.global.drawTool
                  }
                >
                  <ToolbarButton
                    onClick={() => {
                      if (drawToolSelected === "zone") {
                        onToolChange("zone");
                      } else {
                        onToolChange("draw");
                      }
                    }}
                    isActive={activeTool === "draw" || activeTool === "zone"}
                  >
                    {drawToolSelected === "zone" ? (
                      <BoxSelect size={18} />
                    ) : drawToolSelected === "circle" ? (
                      <Circle size={18} />
                    ) : (
                      <Square size={18} />
                    )}
                  </ToolbarButton>
                </EditorTooltip>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={classes.ChevronButton}
                      aria-label="Open draw shape options"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center">
                    <DropdownMenuItem
                      onClick={handleDrawToolChange.bind(null, "rectangle")}
                    >
                      <div className="flex items-center gap-2">
                        <Square size={18} />
                        Rectangle
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleDrawToolChange.bind(null, "circle")}
                    >
                      <div className="flex items-center gap-2">
                        <Circle size={18} />
                        Circle
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleDrawToolChange.bind(null, "zone")}
                    >
                      <div className="flex items-center gap-2">
                        <BoxSelect size={18} />
                        Zone / Room
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="flex items-center">
                <EditorTooltip
                  asChild
                  message={
                    wallToolSelected === "wall-rect"
                      ? "Wall Rectangle"
                      : wallToolSelected === "wall-path"
                        ? "Wall Path"
                        : "Wall Tool"
                  }
                  shortcut={shortcuts.global.wallPathTool}
                >
                  <ToolbarButton
                    onClick={() => onToolChange(wallToolSelected)}
                    isActive={
                      activeTool === "wall" ||
                      activeTool === "wall-rect" ||
                      activeTool === "wall-path"
                    }
                  >
                    {wallToolSelected === "wall-rect" ? (
                      <SquareDashedBottom size={18} />
                    ) : wallToolSelected === "wall-path" ? (
                      <Spline size={18} />
                    ) : (
                      <Minus size={18} />
                    )}
                  </ToolbarButton>
                </EditorTooltip>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={classes.ChevronButton}
                      aria-label="Open wall tool options"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center">
                    <DropdownMenuItem
                      onClick={handleWallToolChange.bind(null, "wall")}
                    >
                      <div className="flex items-center gap-2">
                        <Minus size={18} />
                        Wall
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleWallToolChange.bind(null, "wall-rect")}
                    >
                      <div className="flex items-center gap-2">
                        <SquareDashedBottom size={18} />
                        Wall Rectangle
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleWallToolChange.bind(null, "wall-path")}
                    >
                      <div className="flex items-center gap-2">
                        <Spline size={18} />
                        Wall Path
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <EditorTooltip asChild message={"Measure & Set Scale"}>
                <ToolbarButton
                  onClick={onToolChange.bind(null, "measure")}
                  isActive={activeTool === "measure"}
                >
                  <Ruler size={18} />
                </ToolbarButton>
              </EditorTooltip>

              <EditorTooltip asChild message={"Auto Zone (detect rooms)"}>
                <ToolbarButton
                  onClick={onToolChange.bind(null, "auto-zone")}
                  isActive={activeTool === "auto-zone"}
                >
                  <Sparkles size={18} />
                </ToolbarButton>
              </EditorTooltip>

              <EditorTooltip asChild message={"Grid (e.g. parking lots)"}>
                <ToolbarButton
                  onClick={onToolChange.bind(null, "grid")}
                  isActive={activeTool === "grid"}
                >
                  <Grid3x3 size={18} />
                </ToolbarButton>
              </EditorTooltip>

              <EditorTooltip asChild message={"Text Tool"}>
                <ToolbarButton
                  onClick={onToolChange.bind(null, "text")}
                  isActive={activeTool === "text"}
                >
                  <Type size={18} />
                </ToolbarButton>
              </EditorTooltip>

              <EditorTooltip asChild message={"Label Tool"}>
                <ToolbarButton
                  onClick={onToolChange.bind(null, "numbering")}
                  isActive={activeTool === "numbering"}
                >
                  <ArrowDown01 size={18} />
                </ToolbarButton>
              </EditorTooltip>

              <div className={classes.Separator} />

              {/* Asset tools */}
              <Popover
                open={iconPickerDisclosure.isOpen}
                onOpenChange={(isOpen) => {
                  if (!isOpen) {
                    iconPickerDisclosure.close();
                  }
                }}
              >
                <PopoverTrigger>
                  <EditorTooltip
                    message={"Icon"}
                    shortcut={shortcuts.global.iconTool}
                  >
                    <ToolbarButton
                      onClick={handleIconToolClick}
                      isActive={activeTool === "icon"}
                      hasChevron
                    >
                      {iconToolSelectedIcon ? (
                        (() => {
                          const iconUrl =
                            apiIconCustomSets[iconToolSelectedIcon.set]?.urls?.[
                              iconToolSelectedIcon.icon
                            ];
                          if (iconUrl) {
                            return (
                              <img
                                src={iconUrl}
                                alt={iconToolSelectedIcon.icon}
                                width={20}
                                height={20}
                              />
                            );
                          }
                          return (
                            <IconFromSet icon={iconToolSelectedIcon} size={5} />
                          );
                        })()
                      ) : (
                        <Boxes size={18} />
                      )}
                    </ToolbarButton>
                  </EditorTooltip>
                </PopoverTrigger>
                <PopoverContent
                  arrowProps={{ className: classes.PopoverArrow }}
                  className={classes.PopoverContent}
                >
                  <IconPicker
                    onSelect={handleIconSelection}
                    initialIconSet={"tactic"}
                    customSets={apiIconCustomSets}
                    onUploadIcon={onUploadIcon}
                  />
                </PopoverContent>
              </Popover>

              <EditorTooltip asChild message={"Furniture"}>
                <ToolbarButton
                  onClick={handleFurnitureToolClick}
                  isActive={activeTool === "furniture"}
                  hasChevron
                >
                  <Armchair size={18} />
                </ToolbarButton>
              </EditorTooltip>

              <div className={classes.Separator} />
            </>
          )}

          {/* Resource tools */}
          <div className="flex items-center">
            {nodeToolSelected === "spot" && (
              <EditorTooltip asChild message={"Resource Creator"}>
                <ToolbarButton
                  onClick={onToolChange.bind(null, "spot")}
                  isActive={activeTool === "spot"}
                >
                  <Circle size={18} />
                </ToolbarButton>
              </EditorTooltip>
            )}
            {nodeToolSelected === "node-style" && (
              <EditorTooltip asChild message={"Node Style"}>
                <ToolbarButton
                  onClick={onToolChange.bind(null, "node-style")}
                  isActive={activeTool === "node-style"}
                >
                  <Eclipse size={18} />
                </ToolbarButton>
              </EditorTooltip>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={classes.ChevronButton}
                  aria-label="Open resource tool options"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center">
                <DropdownMenuItem
                  onClick={handleNodeToolChange.bind(null, "spot")}
                >
                  <div className="flex items-center gap-2">
                    <Circle size={18} />
                    Resource Creator
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleNodeToolChange.bind(null, "node-style")}
                >
                  <div className="flex items-center gap-2">
                    <Eclipse size={18} />
                    Resource Style
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {!restrictedMode && (
            <>
              <div className={classes.Separator} />

              <EditorTooltip
                asChild
                message="Keyboard Shortcuts"
                shortcut="shift+/"
              >
                <ToolbarButton
                  onClick={() => setShortcutsDialogOpen(true)}
                  isActive={shortcutsDialogOpen}
                >
                  <Keyboard size={18} />
                </ToolbarButton>
              </EditorTooltip>
            </>
          )}
        </div>
      </TooltipProvider>
      <KeyboardShortcutsDialog
        open={shortcutsDialogOpen}
        onOpenChange={setShortcutsDialogOpen}
      />
      <FurnitureSelectionModal
        open={furniturePickerDisclosure.isOpen}
        onClose={furniturePickerDisclosure.close}
        onSelect={handleFurnitureSelection}
        furnitureList={
          apiFurnitureList.length > 0 ? apiFurnitureList : undefined
        }
        onUpload={
          onUploadIcon
            ? (name, svg, category) => onUploadIcon(name, svg, category)
            : undefined
        }
      />
    </>
  );
};

export const ToolsPickerToolbar = React.memo(ToolsPickerToolbarComponent);
